import { Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useMediaQuery } from "react-responsive"
import { useNavigate } from "react-router-dom"
import { useRecoilState } from "recoil"
import Stepper from "src/components/atom/Stepper"
import { MEMBER_TYPE, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { AuthenticationService, ProfileService } from "src/openapi"
import { AddressService } from "src/openapi_address"
import { ShowHeader } from "src/states/showHeader"
import { ShowProgress } from "src/states/showProgress"
import { ShowToast } from "src/states/showToast"
import { Box, Typography } from "src/UILibrary"
import {
  validateGeneralInformation,
  validateLoginInformation,
  validatePRInformation,
} from "../CompanyValidation"
import { CompanyGeneralInformation } from "./CompanyGeneralInformation"
import { CompanyLoginInformation } from "./CompanyLoginInformation"
import { CompanyPRInformation } from "./CompanyPRInformation"

const STEP = {
  GENERAL_INFORMATION: 1,
  LOGIN_INFORMATION: 2,
  PR_INFORMATION: 3,
}

export const CompanyRegisterPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [step, setStep] = useState<number>(STEP.GENERAL_INFORMATION)

  const navigate = useNavigate()

  const [companyName, setCompanyName] = useState<string>("")
  const [companyNameJP, setCompanyNameJP] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameJP, setFirstNameJP] = useState<string>("")
  const [lastNameJP, setLastNameJP] = useState<string>("")
  const [areaID, setAreaID] = useState<number>(1)
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [headPostCode, setHeadPostCode] = useState<string>("")
  const [headProvince, setHeadProvince] = useState<string>("北海道")
  const [headCity, setHeadCity] = useState<string>("")
  const [headStreet, setHeadStreet] = useState<string>("")
  const [headBuilding, setHeadBuilding] = useState<string>("")
  const [headSiteURL, setHeadSiteURL] = useState<string>("")
  const [headPhoneNumber, setHeadPhoneNumber] = useState<string>("")
  const [officePostCode, setOfficePostCode] = useState<string>("")
  const [officeProvince, setOfficeProvince] = useState<string>("北海道")
  const [officeCity, setOfficeCity] = useState<string>("")
  const [officeStreet, setOfficeStreet] = useState<string>("")
  const [officeBuilding, setOfficeBuilding] = useState<string>("")
  const [officeSiteURL, setOfficeSiteURL] = useState<string>("")
  const [officePhoneNumber, setOfficePhoneNumber] = useState<string>("")
  const [companyNameValidationMessage, setCompanyNameValidationMessage] = useState<string>("")
  const [companyNameJPValidationMessage, setCompanyNameJPValidationMessage] = useState<string>("")
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [headPostCodeValidationMessage, setHeadPostCodeValidationMessage] = useState<string>("")
  const [headCityValidationMessage, setHeadCityValidationMessage] = useState<string>("")
  const [headSiteUrlValidationMessage, setHeadSiteUrlValidationMessage] = useState<string>("")
  const [headPhoneNumberValidationMessage, setHeadPhoneNumberValidationMessage] =
    useState<string>("")
  const [officePostCodeValidationMessage, setOfficePostCodeValidationMessage] = useState<string>("")
  const [officeSiteUrlValidationMessage, setOfficeSiteUrlValidationMessage] = useState<string>("")
  const [officePhoneNumberValidationMessage, setOfficePhoneNumberValidationMessage] =
    useState<string>("")

  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [passwordConfirm, setPasswordConfirm] = useState<string>("")
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")
  const [passwordConfirmValidationMessage, setPasswordConfirmValidationMessage] =
    useState<string>("")

  const [startDate, setStartDate] = useState<Dayjs | null>(null)
  const [capital, setCapital] = useState<string>("")
  const [anual, setAnual] = useState<string>("")
  const [employeeCnt, setEmployCnt] = useState<string>("")
  const [companyType, setCompanyType] = useState<number>()
  const [service, setService] = useState<string>("")
  const [capitalValidationMessage, setCapitalValidationMessage] = useState<string>("")
  const [anualValidationMessage, setAnualValidationMessage] = useState<string>("")
  const [employeeCntValidationMessage, setEmployeeCntValidationMessage] = useState<string>("")
  const [companyTypeValidationMessage, setCompanyTypeValidationMessage] = useState<string>("")

  const [lounge, setLounge] = useState<boolean>(false)
  const [loungeValidationMessage, setLoungeValidationMessage] = useState<string>("")

  useEffect(() => {
    setShowHeader(false)
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const handleHeadPostCodeInput = () => {
    if (!headPostCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(headPostCode).then((res) => {
      if (res.prefecture) {
        setHeadProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setHeadCity(city)
      }
    })
  }

  const handleOfficePostCodeInput = () => {
    if (!officePostCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(officePostCode).then((res) => {
      if (res.prefecture) {
        setOfficeProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setOfficeCity(city)
      }
    })
  }

  const confirmGeneralInformation = async () => {
    if (
      !(await validateGeneralInformation(
        companyName,
        companyNameJP,
        firstName,
        lastName,
        firstNameJP,
        lastNameJP,
        phoneNumber,
        headPostCode,
        headCity,
        headSiteURL,
        headPhoneNumber,
        officePostCode,
        officeSiteURL,
        officePhoneNumber,
        setCompanyNameValidationMessage,
        setCompanyNameJPValidationMessage,
        setNameValidationMessage,
        setPhoneNumberValidationMessage,
        setHeadPostCodeValidationMessage,
        setHeadCityValidationMessage,
        setHeadSiteUrlValidationMessage,
        setHeadPhoneNumberValidationMessage,
        setOfficePostCodeValidationMessage,
        setOfficeSiteUrlValidationMessage,
        setOfficePhoneNumberValidationMessage,
        t
      ))
    ) {
      return
    }
    setStep(STEP.LOGIN_INFORMATION)
  }

  const confirmLoginInformation = async () => {
    if (
      !(await validateLoginInformation(
        email,
        password,
        passwordConfirm,
        setEmailValidationMessage,
        setPasswordValidationMessage,
        setPasswordConfirmValidationMessage,
        t
      ))
    ) {
      return
    }

    setStep(STEP.PR_INFORMATION)
  }

  const handleRegister = async () => {
    if (
      !(await validatePRInformation(
        capital,
        anual,
        employeeCnt,
        companyType,
        setCapitalValidationMessage,
        setAnualValidationMessage,
        setEmployeeCntValidationMessage,
        setCompanyTypeValidationMessage,
        lounge,
        setLoungeValidationMessage,
        t
      ))
    ) {
      return
    }

    setShowProgress(true)
    AuthenticationService.signUp({
      email: email,
      password: password,
      userType: MEMBER_TYPE.COMPANY,
    })
      .then((res) => {
        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          ProfileService.createCompany({
            userID: res.data?.UserSub as string,
            email: email,
            companyName: companyName,
            companyNameKana: companyNameJP,
            managerFirstName: firstName,
            managerLastName: lastName,
            managerFirstNameKana: firstNameJP,
            managerLastNameKana: lastNameJP,
            phoneNumber: phoneNumber,
            areaId: areaID,
            headOfficeZipCode: headPostCode,
            headOfficeProvince: headProvince,
            headOfficeCity: headCity,
            headOfficeStreetNo: headStreet,
            headOfficeBuilding: headBuilding,
            headOfficePhoneNumber: headPhoneNumber,
            headOfficeUrl: headSiteURL,
            officeZipCode: officePostCode,
            officeProvince: officeProvince,
            officeCity: officeCity,
            officeStreetNo: officeStreet,
            officeBuilding: officeBuilding,
            officeUrl: officeSiteURL,
            officePhoneNumber: officePhoneNumber,
            startupDate: startDate?.isValid() ? startDate.format("YYYY-MM-DD") : undefined,
            capitalFund: parseFloat(capital),
            annualSales: parseFloat(anual),
            employeeCount: parseFloat(employeeCnt),
            businessContent: service,
            businessTypeId: companyType as number,
            isLoungeMember: lounge,
            comment: "COMPANY_REGISTER",
          })
            .then((res) => {
              setShowProgress(false)
              if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
                setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
                navigate("/mypage/users/list/company")
                navigate(0)
              } else {
                setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
              }
            })
            .catch(() => {
              setShowProgress(false)
              setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
            })
        } else if (res.status === RESPONSE_CODE.CONFLICT) {
          setShowProgress(false)
          setShowToast({ show: true, text: t("already_registered"), type: SNACKBAR_TYPES.WARNING })
        } else {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Box sx={{ px: "20px", pt: "20px" }}>
        <Typography.Large
          color="textPrimary"
          sx={{
            mb: "40px",
          }}
        >
          {t("signup_company", { step: step })}
        </Typography.Large>
        <Stepper
          steps={[t("company_step_1"), t("company_step_2"), t("company_step_3")]}
          current={step}
          sx={{ mb: "60px" }}
        ></Stepper>
        {step === STEP.GENERAL_INFORMATION && (
          <CompanyGeneralInformation
            companyName={companyName}
            companyNameJP={companyNameJP}
            handleCompanyName={setCompanyName}
            handleCompanyNameJP={setCompanyNameJP}
            firstName={firstName}
            lastName={lastName}
            firstNameJP={firstNameJP}
            lastNameJP={lastNameJP}
            handleFirstName={setFirstName}
            handleLastName={setLastName}
            handleFirstNameJP={setFirstNameJP}
            handleLastNameJP={setLastNameJP}
            areaID={areaID}
            handleAreaID={setAreaID}
            phoneNumber={phoneNumber}
            handlePhoneNumber={setPhoneNumber}
            headPostCode={headPostCode}
            handleHeadPostCode={setHeadPostCode}
            headProvince={headProvince}
            handleHeadProvince={setHeadProvince}
            headCity={headCity}
            handleHeadCity={setHeadCity}
            headStreeet={headStreet}
            handleHeadStreet={setHeadStreet}
            headBuilding={headBuilding}
            handleHeadBuilding={setHeadBuilding}
            headSiteURL={headSiteURL}
            handleHeadSiteURL={setHeadSiteURL}
            headPhoneNumber={headPhoneNumber}
            handleHeadPhoneNumber={setHeadPhoneNumber}
            officePostCode={officePostCode}
            handleOfficePostCode={setOfficePostCode}
            officeProvince={officeProvince}
            handleOfficeProvince={setOfficeProvince}
            officeCity={officeCity}
            handleOfficeCity={setOfficeCity}
            officeStreeet={officeStreet}
            handleOfficeStreet={setOfficeStreet}
            officeBuilding={officeBuilding}
            handleOfficeBuilding={setOfficeBuilding}
            officeSiteURL={officeSiteURL}
            handleOfficeSiteURL={setOfficeSiteURL}
            officePhoneNumber={officePhoneNumber}
            handleOfficePhoneNumber={setOfficePhoneNumber}
            companyNameValidationMessage={companyNameValidationMessage}
            companyNameJPValidationMessage={companyNameJPValidationMessage}
            nameValidationMessage={nameValidationMessage}
            phoneNumberValidationMessage={phoneNumberValidationMessage}
            headPostCodeValidationMessage={headPostCodeValidationMessage}
            headCityValidationMessage={headCityValidationMessage}
            headSiteURLValidationMessage={headSiteUrlValidationMessage}
            headPhoneNumberValidationMessage={headPhoneNumberValidationMessage}
            officePostCodeValidationMessage={officePostCodeValidationMessage}
            officeSiteURLValidationMessage={officeSiteUrlValidationMessage}
            officePhoneNumberValidationMessage={officePhoneNumberValidationMessage}
            handleHeadPostCodeInput={handleHeadPostCodeInput}
            handleOfficePostCodeInput={handleOfficePostCodeInput}
            handleNext={() => confirmGeneralInformation()}
          />
        )}
        {step === STEP.LOGIN_INFORMATION && (
          <CompanyLoginInformation
            email={email}
            password={password}
            passwordConfirm={passwordConfirm}
            handleEmail={setEmail}
            handlePassword={setPassword}
            handlePasswordConfirm={setPasswordConfirm}
            emailValidationMessage={emailValidationMessage}
            passwordValidationMessage={passwordValidationMessage}
            passwordConfirmValidationMessage={passwordConfirmValidationMessage}
            handlePrev={() => setStep(STEP.GENERAL_INFORMATION)}
            handleNext={() => confirmLoginInformation()}
          />
        )}
        {step === STEP.PR_INFORMATION && (
          <CompanyPRInformation
            startDate={startDate}
            handleStartDate={setStartDate}
            capital={capital}
            handleCapital={setCapital}
            anual={anual}
            handleAnual={setAnual}
            employeeCnt={employeeCnt}
            handleEmployeeCnt={setEmployCnt}
            companyType={companyType}
            handleCompanyType={setCompanyType}
            service={service}
            handleService={setService}
            capitalValidationMessage={capitalValidationMessage}
            anualValidationMessage={anualValidationMessage}
            employeeCntValidationMessage={employeeCntValidationMessage}
            companyTypeValidationMessage={companyTypeValidationMessage}
            lounge={lounge}
            handleLounge={setLounge}
            loungeValidationMessage={loungeValidationMessage}
            handlePrev={() => setStep(STEP.LOGIN_INFORMATION)}
            handleRegister={() => handleRegister()}
          />
        )}
      </Box>
    </Box>
  )
}
